import { logger, type Tree, visitNotIgnoredFiles } from "@nx/devkit";
import * as ts from "typescript";

const TSX_EXTENSIONS = [".tsx", ".jsx"] as const;
const TS_EXTENSIONS = [".ts", ".tsx", ".jsx"] as const;

const isTsxFile = (filePath: string): boolean => TSX_EXTENSIONS.some(ext => filePath.endsWith(ext));
const isTsFile = (filePath: string): boolean => TS_EXTENSIONS.some(ext => filePath.endsWith(ext));

const isUnderNodeModules = (filePath: string): boolean =>
  filePath.includes("/node_modules/") || filePath.startsWith("node_modules/");

const isUnderDist = (filePath: string): boolean =>
  filePath.includes("/dist/") || filePath.startsWith("dist/") || filePath.includes("/.nx/");

/**
 * Visits every `.tsx`/`.jsx` file under the workspace root and invokes the
 * callback with the file path and its current contents. Files that don't
 * include the marker substring are skipped, which makes large workspaces
 * cheap to scan.
 */
export const visitTsxFiles = (
  tree: Tree,
  marker: string,
  callback: (filePath: string, content: string) => string | null | undefined
): number => {
  let touched = 0;
  visitNotIgnoredFiles(tree, "/", filePath => {
    if (!isTsxFile(filePath)) return;
    if (isUnderNodeModules(filePath) || isUnderDist(filePath)) return;
    const content = tree.read(filePath, "utf-8");
    if (content === null) return;
    if (!content.includes(marker)) return;
    const updated = callback(filePath, content);
    if (updated !== null && updated !== undefined && updated !== content) {
      tree.write(filePath, updated);
      touched += 1;
    }
  });
  return touched;
};

/**
 * Same as {@link visitTsxFiles} but also includes plain `.ts` files. Use this
 * when the codemod also rewrites non-JSX files such as helpers or hooks.
 */
export const visitTsFiles = (
  tree: Tree,
  marker: string,
  callback: (filePath: string, content: string) => string | null | undefined
): number => {
  let touched = 0;
  visitNotIgnoredFiles(tree, "/", filePath => {
    if (!isTsFile(filePath)) return;
    if (isUnderNodeModules(filePath) || isUnderDist(filePath)) return;
    const content = tree.read(filePath, "utf-8");
    if (content === null) return;
    if (!content.includes(marker)) return;
    const updated = callback(filePath, content);
    if (updated !== null && updated !== undefined && updated !== content) {
      tree.write(filePath, updated);
      touched += 1;
    }
  });
  return touched;
};

/**
 * Mapping of imported component aliases to their original names for a given
 * package. For `import { IconButton as Btn } from "@trackunit/react-components"`
 * this returns `{ Btn: "IconButton" }`.
 *
 * Returns `null` when the file imports nothing from the package, which lets
 * callers short-circuit before parsing JSX.
 */
export const getImportedAliases = (sourceFile: ts.SourceFile, packageName: string): Record<string, string> | null => {
  const result: Record<string, string> = {};
  let found = false;

  for (const stmt of sourceFile.statements) {
    if (!ts.isImportDeclaration(stmt)) continue;
    const moduleSpecifier = stmt.moduleSpecifier;
    if (!ts.isStringLiteral(moduleSpecifier)) continue;
    if (moduleSpecifier.text !== packageName) continue;

    const namedBindings = stmt.importClause?.namedBindings;
    if (namedBindings === undefined || !ts.isNamedImports(namedBindings)) continue;

    for (const element of namedBindings.elements) {
      const localName = element.name.text;
      const importedName = element.propertyName?.text ?? localName;
      result[localName] = importedName;
      found = true;
    }
  }

  return found ? result : null;
};

/**
 * Returns the local alias used in this file for `originalName` when imported
 * from `packageName`, or `null` if the component is not imported.
 */
export const getLocalAliasFor = (
  sourceFile: ts.SourceFile,
  packageName: string,
  originalName: string
): string | null => {
  const aliases = getImportedAliases(sourceFile, packageName);
  if (aliases === null) return null;
  for (const [local, original] of Object.entries(aliases)) {
    if (original === originalName) return local;
  }
  return null;
};

export type JsxElementMatch = {
  /** The opening JSX element (the one carrying the attributes). */
  openingElement: ts.JsxOpeningElement | ts.JsxSelfClosingElement;
  /** The full element including children, or the self-closing element. */
  element: ts.JsxElement | ts.JsxSelfClosingElement;
};

/**
 * Finds every JSX element whose tag name resolves to one of `tagNames`
 * (typically the local aliases returned by {@link getImportedAliases}).
 *
 * The result intentionally includes both opening and full elements so callers
 * can manipulate attributes (via `openingElement`) or wrap/replace the whole
 * element (via `element`).
 */
export const findJsxElements = (
  sourceFile: ts.SourceFile,
  tagNames: ReadonlyArray<string>
): ReadonlyArray<JsxElementMatch> => {
  const matches: Array<JsxElementMatch> = [];
  const tagSet = new Set(tagNames);

  const visit = (node: ts.Node): void => {
    if (ts.isJsxSelfClosingElement(node)) {
      if (ts.isIdentifier(node.tagName) && tagSet.has(node.tagName.text)) {
        matches.push({ openingElement: node, element: node });
      }
    } else if (ts.isJsxElement(node)) {
      const opening = node.openingElement;
      if (ts.isIdentifier(opening.tagName) && tagSet.has(opening.tagName.text)) {
        matches.push({ openingElement: opening, element: node });
      }
    }
    ts.forEachChild(node, visit);
  };

  visit(sourceFile);
  return matches;
};

/**
 * Looks up a named JSX attribute on the opening element. Returns `null` when
 * the attribute is missing or part of a spread attribute (which we cannot
 * statically inspect).
 */
export const findJsxAttribute = (
  openingElement: ts.JsxOpeningElement | ts.JsxSelfClosingElement,
  attributeName: string
): ts.JsxAttribute | null => {
  for (const attr of openingElement.attributes.properties) {
    if (!ts.isJsxAttribute(attr)) continue;
    if (!ts.isIdentifier(attr.name)) continue;
    if (attr.name.text === attributeName) return attr;
  }
  return null;
};

/**
 * `true` when the element forwards a spread expression such as `{...rest}`,
 * which means we can't be sure which attributes are actually set.
 */
export const hasSpreadAttribute = (openingElement: ts.JsxOpeningElement | ts.JsxSelfClosingElement): boolean => {
  return openingElement.attributes.properties.some(prop => ts.isJsxSpreadAttribute(prop));
};

/**
 * Parses the file as TSX so JSX is recognised. We do not need a full program
 * here; tsquery's `ast` helper produces a script-kind source file which loses
 * JSX context, so we go through `createSourceFile` directly.
 */
export const parseTsx = (content: string, fileName = "file.tsx"): ts.SourceFile =>
  ts.createSourceFile(fileName, content, ts.ScriptTarget.Latest, true, ts.ScriptKind.TSX);

/**
 * Helper for codemods to log a one-line summary at the end of a run. Migration
 * runners aggregate logs per migration, so this keeps output focused.
 */
export const logSummary = (migrationName: string, touched: number, manualReviewNeeded?: number): void => {
  if (touched === 0) {
    logger.info(`  ${migrationName}: no files changed`);
    return;
  }
  const reviewSuffix =
    manualReviewNeeded !== undefined && manualReviewNeeded > 0
      ? ` (${manualReviewNeeded} location(s) need manual review)`
      : "";
  logger.info(`  ${migrationName}: updated ${touched} file(s)${reviewSuffix}`);
};

/**
 * Inserts an attribute string immediately after the tag name (so the new
 * attribute appears first). Returns the resulting source content.
 *
 * The added attribute is rendered verbatim, so callers are responsible for
 * including the leading space (e.g. ` title="Close"`).
 */
export const insertAttributeIntoOpeningTag = (
  content: string,
  openingElement: ts.JsxOpeningElement | ts.JsxSelfClosingElement,
  attributeText: string
): string => {
  const tagName = openingElement.tagName;
  const insertPos = tagName.getEnd();
  return `${content.slice(0, insertPos)} ${attributeText}${content.slice(insertPos)}`;
};
